import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  Label,
  Textarea
} from '@invent/wl-ui-kit-next/ui';
import { ChevronDown } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ChoiceSelect,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  NotPermitted
} from '@/design';
import { ApiError, ProblemType, type IEtagged } from '@/domains/api';
import type {
  IListFormattingRule,
  IListQuery,
  ISavedView,
  ISavedViewBody,
  SavedViewKind,
  SavedViewVisibility
} from '../../api/lists-contracts';
import { listsApi } from '../../api/lists-api';
import { TEAM_PATHS, reportPath } from '../../paths';
import { refusalMessage } from '../../refusal';
import { LIST_PAGE_SIZES, VISIBILITIES, listSurveyLabel, visibilityLabel } from '../../lists';

/**
 * A view's saves (SR-230, design `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md`
 * §3.3) — *Save view ▾*: **Save** the whole view under `If-Match`, **Save as…** a new one,
 * **Rename…**, **Formatting…**, **Make default for <survey>**, **Delete…** (confirmed; absent on a
 * seed). One set for Lists and, with `kind: report`, for Reports (principle 7).
 *
 * **Save view is not Save data** (principle 3): this writes the view — its filter, columns, sorts,
 * grouping, page size and rules as the screen holds them — and never a cell. **A save is taken in
 * place** (`onAdopt`), never by opening the view afresh: the screen keeps the cells being edited,
 * and *Changed — not saved* is compared with the view as saved. *Rename…* and *Make default* send
 * the view **as saved**, the name or the default changed — the screen's unsaved changes stay
 * unsaved, which is what they are.
 *
 * **Who may is the read's `mayEdit` and `useCan()`, and survey-api judges** (§3.12;
 * `SavedViewRules`): where `mayEdit` is false *Save* is replaced by **Save as my private view** —
 * the offer, never a refusal after the fact; Shared, Shared read-only and a default are offered
 * under `lists.share` and disabled with `NotPermitted`'s sentence without it; a default is a shared
 * or read-only view's (survey-api's 400 at `isDefault`), so a private view's *Make default* is
 * disabled with that reason. A seed stays shared (its 400 at `visibility`) and is never deleted.
 *
 * **Every refusal is said where it belongs** (`viewRefusal`): 409 `revision-conflict` atop the
 * toolbar with *Reload* — the view as saved now, the screen's changes kept, unsaved; 403 with the
 * offer; a 400 by path — a dialog's own boxes in the dialog, the query's on the chips and builder
 * rows (`problems`, which the screen places), a rule's in *Formatting…*; 409 `seeded-view`; 404 the
 * view gone; 428 this page's fault; an answer that never arrived as one to check.
 */

/** A default in words — "the default for FA", "the default FA report". */
function defaultWords(kind: SavedViewKind, surveyLabel: string): string {
  return kind === 'report' ? `the default ${surveyLabel} report` : `the default for ${surveyLabel}`;
}

/** Why *Make default* is not offered on a view, or null where it is. */
function whyNotDefault(view: Nullable<ISavedView>, kind: SavedViewKind, surveyLabel: string): Nullable<string> {
  if (!view) return null;
  if (view.isDefault) return `Already ${defaultWords(kind, surveyLabel)}`;
  if (view.visibility === 'private') return 'Only a shared view can be the default';
  return null;
}

/** What a save sends of the screen: its query, page size and rules. */
export interface IViewScreenBody {
  query: IListQuery;
  pageSize: Nullable<number>;
  formattingRules: IListFormattingRule[];
}

/** A refusal's place: the dialog's boxes, the screen's (`query.…`, `formattingRules[…]`). */
export interface IViewProblem {
  target: string;
  reason: string;
}

/** A saved view's query as a save sends it: the view it names is none (survey-api's 400 at
 *  `query.viewId`). */
function savedQuery(query: IListQuery): IListQuery {
  const { viewId: _named, ...rest } = query;
  return rest;
}

/** The view as saved, as a body — what *Rename…* and *Make default* change one member of. */
export function storedBody(view: ISavedView): ISavedViewBody {
  return {
    kind: view.kind,
    name: view.name,
    description: view.description,
    visibility: view.visibility,
    isDefault: view.isDefault,
    pageSize: view.pageSize,
    query: savedQuery(view.query),
    formattingRules: view.formattingRules
  };
}

/** A save's answer in words, and what the line offers. */
export interface IViewSaid {
  text: string;
  alert: boolean;
  /** *Reload* — the view as saved now, the screen's changes kept. */
  reload?: boolean;
  /** *Save as my private view*. */
  offerPrivate?: boolean;
}

const DIALOG_TARGETS = ['name', 'description', 'visibility', 'isDefault', 'pageSize'];

/** Whether a refusal lands on one of a dialog's own boxes. */
function onDialog(target: string): boolean {
  return DIALOG_TARGETS.some((box) => target === box || target.startsWith(`${box}.`));
}

/**
 * A view write's refusal, by status and `type` (§3.3). `problems` are a 400's, for the screen and
 * the dialog to place; `said` the line atop the toolbar.
 */
export function viewRefusal(
  error: unknown,
  doing: string
): { said: IViewSaid; problems: IViewProblem[] } {
  if (!(error instanceof ApiError) || error.status >= 500) {
    return {
      said: {
        text: `The answer did not arrive — reload the view to see whether ${doing} landed.`,
        alert: true,
        reload: true
      },
      problems: []
    };
  }
  const type = error.problem?.type;
  if (error.status === 409 && type === ProblemType.revisionConflict) {
    return {
      said: {
        text: 'This view was changed by someone else since you opened it. Reload it — your changes stay here, unsaved.',
        alert: true,
        reload: true
      },
      problems: []
    };
  }
  if (error.status === 409 && type === ProblemType.seededView) {
    return { said: { text: 'A seeded view is never deleted.', alert: true }, problems: [] };
  }
  if (error.status === 403) {
    return {
      said: {
        text: 'You may open this view but not change it — save your changes as your private view.',
        alert: true,
        offerPrivate: true
      },
      problems: []
    };
  }
  if (error.status === 404) {
    return {
      said: {
        text: 'This view is no longer there — deleted since you opened it. Save your changes as a new view to keep them.',
        alert: true,
        offerPrivate: true
      },
      problems: []
    };
  }
  if (error.status === 428) {
    return {
      said: {
        text: 'This page sent the save without the view’s revision — reload the view, then save again.',
        alert: true,
        reload: true
      },
      problems: []
    };
  }
  const problems = error.problem?.problems ?? [];
  if (error.status === 400 && problems.length > 0) {
    return { said: { text: `The view could not be saved.`, alert: true }, problems };
  }
  return { said: { text: refusalMessage(error, 'The view could not be saved.'), alert: true }, problems: [] };
}

/** The screen's refusals said in words, the dialog's left to its boxes. */
function screenWords(problems: readonly IViewProblem[], say: (problem: IViewProblem) => string): string {
  const words = problems.filter((problem) => !onDialog(problem.target)).map(say);
  return [...new Set(words)].join('; ');
}

export function useViewSaves({
  kind,
  survey,
  saved,
  changed,
  screen,
  pageSize,
  canShare,
  sayProblem,
  onAdopt,
  onCreated,
  onDeleted,
  onFormatting,
  menuLabel = 'Save view',
  moreItems
}: {
  kind: SavedViewKind;
  survey: string;
  /** The view as saved, with the `ETag` a save quotes — null for a bare list. */
  saved: Nullable<IEtagged<ISavedView>>;
  changed: boolean;
  screen: () => IViewScreenBody;
  /** The page size on screen, Save as…'s default. */
  pageSize: number;
  canShare: boolean;
  /** A refusal of the query or a rule in words — the screen's own sentence for it. */
  sayProblem: (problem: IViewProblem) => string;
  /** A save taken in place. */
  onAdopt: (view: IEtagged<ISavedView>) => void;
  /** *Save as…*'s new view — the screen moves to it, kept as it is. */
  onCreated: (view: IEtagged<ISavedView>) => void;
  /** The view deleted — the screen stays as shown, a view no longer. */
  onDeleted: () => void;
  onFormatting?: () => void;
  /** The menu's button — a report's header says *Save* (§4.2). */
  menuLabel?: string;
  /** Entries of the caller's, before *Delete…* — a report's *Open as a list* (Q7). */
  moreItems?: React.ReactNode;
}) {
  const view = saved?.body ?? null;
  const noun = kind === 'report' ? 'report' : 'view';
  const [said, setSaid] = React.useState<Nullable<IViewSaid>>(null);
  const [problems, setProblems] = React.useState<IViewProblem[]>([]);
  const [busy, setBusy] = React.useState(false);
  const [dialog, setDialog] = React.useState<Nullable<'saveAs' | 'private' | 'rename' | 'delete'>>(null);
  const shareReason = React.useId();
  const surveyLabel = listSurveyLabel(survey);
  const mayEdit = view?.mayEdit === true;

  const refused = (error: unknown, doing: string) => {
    const refusal = viewRefusal(error, doing);
    setProblems(refusal.problems);
    const words = screenWords(refusal.problems, sayProblem);
    setSaid({ ...refusal.said, text: words ? `${refusal.said.text} ${words}.` : refusal.said.text });
    return refusal;
  };

  /** One `PUT` of the view — the screen's (`Save`) or the view as saved with one member changed.
   *  `boxed` is a refusal of a dialog's own boxes, which the dialog says and stays open for. */
  const put = async (
    body: ISavedViewBody,
    doing: string,
    done: string
  ): Promise<'landed' | 'boxed' | 'refused'> => {
    if (!view || !saved) return 'refused';
    if (saved.etag === null) {
      setSaid({
        text: 'This view came without its revision, so it cannot be saved safely — reload it.',
        alert: true,
        reload: true
      });
      return 'refused';
    }
    setBusy(true);
    setSaid(null);
    setProblems([]);
    try {
      onAdopt(await listsApi.saveView(view.id, saved.etag, body));
      setSaid({ text: done, alert: false });
      return 'landed';
    } catch (error) {
      const refusal = refused(error, doing);
      return refusal.problems.some((problem) => onDialog(problem.target)) ? 'boxed' : 'refused';
    } finally {
      setBusy(false);
    }
  };

  const save = () => {
    if (!view) return;
    const body = screen();
    void put({ ...storedBody(view), ...body, query: savedQuery(body.query) }, 'the save', 'Saved.');
  };

  const reload = async () => {
    if (!view) return;
    setBusy(true);
    try {
      const fresh = await listsApi.openView(view.id);
      if (fresh === null) {
        setSaid({
          text: 'This view is no longer there — deleted since you opened it. Save your changes as a new view to keep them.',
          alert: true,
          offerPrivate: true
        });
        return;
      }
      onAdopt(fresh);
      setProblems([]);
      setSaid({ text: 'The view as saved now is read — your changes are still here, unsaved.', alert: false });
    } catch (error) {
      setSaid({ text: `The view could not be read again: ${refusalMessage(error, 'no answer')}`, alert: true, reload: true });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!view || !saved) return;
    if (saved.etag === null) {
      setSaid({
        text: 'This view came without its revision, so it cannot be deleted safely — reload it.',
        alert: true,
        reload: true
      });
      return;
    }
    setBusy(true);
    setSaid(null);
    try {
      await listsApi.deleteView(view.id, saved.etag);
      onDeleted();
      setSaid({ text: `The ${noun} ${view.name} was deleted — the list stays as shown, not saved.`, alert: false });
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        onDeleted();
        setSaid({ text: `The ${noun} ${view.name} was already deleted.`, alert: false });
      } else {
        refused(error, 'the delete');
      }
    } finally {
      setBusy(false);
    }
  };

  const defaultReason = whyNotDefault(view, kind, surveyLabel);

  const menu = (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="outline" className="h-8 gap-1 px-2.5 font-normal" disabled={busy}>
            {busy ? 'Saving…' : menuLabel}
            <ChevronDown className="size-4" aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="w-80"
          aria-describedby={canShare ? undefined : shareReason}
        >
          {view && mayEdit && (
            <DropdownMenuItem disabled={!changed} onSelect={save}>
              <span className="grid">
                Save
                {!changed && <span className="text-xs text-muted-foreground">Nothing changed since it was opened</span>}
              </span>
            </DropdownMenuItem>
          )}
          {view && !mayEdit && (
            <DropdownMenuItem onSelect={() => setDialog('private')}>
              <span className="grid">
                Save as my private view
                <span className="text-xs text-muted-foreground">
                  {view.visibility === 'shared'
                    ? 'Your Team Portal roles do not include saving over a shared view'
                    : 'Only its owner saves over this view'}
                </span>
              </span>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={() => setDialog('saveAs')}>Save as…</DropdownMenuItem>
          {view && (
            <DropdownMenuItem disabled={!mayEdit} onSelect={() => setDialog('rename')}>
              Rename…
            </DropdownMenuItem>
          )}
          {onFormatting && <DropdownMenuItem onSelect={onFormatting}>Formatting…</DropdownMenuItem>}
          {view && (
            <DropdownMenuItem
              disabled={!mayEdit || !canShare || defaultReason !== null}
              onSelect={() =>
                void put(
                  { ...storedBody(view), isDefault: true },
                  'making it the default',
                  `${view.name} is ${defaultWords(kind, surveyLabel)}.`
                )
              }
            >
              <span className="grid">
                Make {defaultWords(kind, surveyLabel)}
                {defaultReason && <span className="text-xs text-muted-foreground">{defaultReason}</span>}
              </span>
            </DropdownMenuItem>
          )}
          {moreItems}
          {view && (
            <>
              <DropdownMenuSeparator />
              {view.seed !== null ? (
                <DropdownMenuItem disabled>
                  <span className="text-xs text-muted-foreground">A seeded view is never deleted</span>
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem disabled={!mayEdit} onSelect={() => setDialog('delete')}>
                  <span className="grid">
                    Delete…
                    {!mayEdit && (
                      <span className="text-xs text-muted-foreground">Only who may save over a view deletes it</span>
                    )}
                  </span>
                </DropdownMenuItem>
              )}
            </>
          )}
          {!canShare && (
            <div id={shareReason} className="border-t border-border px-2 py-1.5">
              <NotPermitted what="sharing a view or making one the default" className="text-xs" />
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <SaveAsDialog
        open={dialog === 'saveAs' || dialog === 'private'}
        privateOnly={dialog === 'private'}
        kind={kind}
        survey={survey}
        from={view}
        pageSize={pageSize}
        canShare={canShare}
        screen={screen}
        sayProblem={sayProblem}
        onProblems={setProblems}
        onClose={() => setDialog(null)}
        onCreated={(created) => {
          setDialog(null);
          setSaid({ text: `Saved as ${created.body.name}.`, alert: false });
          onCreated(created);
        }}
      />
      {view && (
        <RenameDialog
          open={dialog === 'rename'}
          view={view}
          onClose={() => setDialog(null)}
          onRename={async (name, description) => {
            const answer = await put({ ...storedBody(view), name, description }, 'the rename', 'Renamed.');
            if (answer !== 'boxed') setDialog(null);
          }}
          problems={problems}
        />
      )}
      {view && (
        <AlertDialog open={dialog === 'delete'} onOpenChange={(open) => !open && setDialog(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Delete the {noun} {view.name}?
              </AlertDialogTitle>
              <AlertDialogDescription>
                {view.visibility === 'private'
                  ? 'Nobody else sees it.'
                  : 'Everyone it is shared with loses it too.'}{' '}
                Nothing undoes a delete.{' '}
                {kind === 'report'
                  ? 'Its runs and their files go with it.'
                  : 'The list on screen stays as it is, not saved.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  setDialog(null);
                  void remove();
                }}
              >
                Delete the {noun}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );

  const notice = said && (
    <div
      role={said.alert ? 'alert' : 'status'}
      data-view-said=""
      className={`flex flex-wrap items-center gap-2 text-sm ${said.alert ? 'text-destructive' : 'text-muted-foreground'}`}
    >
      <span>{said.text}</span>
      {said.reload && view && (
        <Button type="button" variant="outline" className="h-7 px-2.5" disabled={busy} onClick={() => void reload()}>
          Reload
        </Button>
      )}
      {said.offerPrivate && (
        <Button type="button" variant="outline" className="h-7 px-2.5" onClick={() => setDialog('private')}>
          Save as my private view
        </Button>
      )}
    </div>
  );

  return { menu, notice, problems, clearProblems: () => setProblems([]) };
}

/** A 400's problems on one of the dialog's boxes, in words. */
function boxProblems(problems: readonly IViewProblem[], box: string): string[] {
  return problems.filter((problem) => problem.target === box).map((problem) => problem.reason);
}

function BoxProblems({ id, reasons }: { id: string; reasons: readonly string[] }) {
  if (reasons.length === 0) return null;
  return (
    <p id={id} className="text-sm text-destructive">
      {reasons.join('; ')}
    </p>
  );
}

/**
 * *Save as…* — a new view of this survey and kind (`kind` and `survey` fixed): name, description,
 * visibility (Private always; Shared and Shared, read-only under `lists.share`), the survey's
 * default (under `lists.share`, a shared view's alone), page size. With `privateOnly` it is *Save as
 * my private view*: Private, the view's name offered.
 */
function SaveAsDialog({
  open,
  privateOnly,
  kind,
  survey,
  from,
  pageSize,
  canShare,
  screen,
  sayProblem,
  onProblems,
  onClose,
  onCreated
}: {
  open: boolean;
  privateOnly: boolean;
  kind: SavedViewKind;
  survey: string;
  from: Nullable<ISavedView>;
  pageSize: number;
  canShare: boolean;
  screen: () => IViewScreenBody;
  sayProblem: (problem: IViewProblem) => string;
  onProblems: (problems: IViewProblem[]) => void;
  onClose: () => void;
  onCreated: (view: IEtagged<ISavedView>) => void;
}) {
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [visibility, setVisibility] = React.useState<SavedViewVisibility>('private');
  const [isDefault, setIsDefault] = React.useState(false);
  const [size, setSize] = React.useState(pageSize);
  const [problems, setProblems] = React.useState<IViewProblem[]>([]);
  const [said, setSaid] = React.useState<Nullable<string>>(null);
  const [busy, setBusy] = React.useState(false);
  const shareReason = React.useId();
  const ids = { name: React.useId(), description: React.useId(), visibility: React.useId() };
  const [wasOpen, setWasOpen] = React.useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setName(privateOnly && from ? from.name : '');
      setDescription(privateOnly && from ? (from.description ?? '') : '');
      setVisibility('private');
      setIsDefault(false);
      setSize(pageSize);
      setProblems([]);
      setSaid(null);
    }
  }
  const surveyLabel = listSurveyLabel(survey);
  const sizes = [...new Set([...LIST_PAGE_SIZES, size])].sort((a, b) => a - b);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setProblems([]);
    setSaid(null);
    const body = screen();
    try {
      onCreated(
        await listsApi.createView({
          kind,
          name,
          description: description.trim() === '' ? null : description,
          visibility,
          isDefault: isDefault && visibility !== 'private',
          pageSize: size,
          query: savedQuery(body.query),
          formattingRules: body.formattingRules
        })
      );
      onProblems([]);
    } catch (error) {
      const refusal = viewRefusal(error, 'the save');
      setProblems(refusal.problems);
      onProblems(refusal.problems);
      const words = screenWords(refusal.problems, sayProblem);
      setSaid(
        !(error instanceof ApiError) || error.status >= 500
          ? 'The answer did not arrive — open the view picker to see whether the view was made before saving again.'
          : error.status === 403
            ? 'Your Team Portal roles do not include sharing a view or making one the default — save it as Private.'
            : refusal.problems.length > 0
              ? words
                ? `The view could not be saved: ${words}. The list’s own marks say where.`
                : null
              : refusal.said.text
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{privateOnly ? 'Save as my private view' : 'Save as a new view'}</DialogTitle>
          <DialogDescription>
            {kind === 'report' ? 'A report' : 'A list'} of {surveyLabel}, as it is shown now — its
            filter, columns, sorts, grouping and colours.
          </DialogDescription>
        </DialogHeader>
        <DialogBody as="form" onSubmit={(event) => void submit(event)} noValidate>
          <div className="grid gap-1.5">
            <Label htmlFor={`${ids.name}-box`}>Name</Label>
            <Input
              id={`${ids.name}-box`}
              value={name}
              onChange={(event) => setName(event.target.value)}
              aria-invalid={boxProblems(problems, 'name').length > 0 || undefined}
              aria-describedby={ids.name}
            />
            <BoxProblems id={ids.name} reasons={boxProblems(problems, 'name')} />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor={`${ids.description}-box`}>Description</Label>
            <Textarea
              id={`${ids.description}-box`}
              value={description}
              rows={2}
              onChange={(event) => setDescription(event.target.value)}
              aria-describedby={ids.description}
            />
            <BoxProblems id={ids.description} reasons={boxProblems(problems, 'description')} />
          </div>
          {!privateOnly && (
            <fieldset className="grid gap-1.5" aria-describedby={canShare ? ids.visibility : `${ids.visibility} ${shareReason}`}>
              <legend className="mb-1 text-sm font-medium">Who sees it</legend>
              {VISIBILITIES.map((one) => (
                <label key={one} className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="view-visibility"
                    value={one}
                    checked={visibility === one}
                    disabled={one !== 'private' && !canShare}
                    onChange={() => {
                      setVisibility(one);
                      if (one === 'private') setIsDefault(false);
                    }}
                    className="accent-[var(--color-teal)]"
                  />
                  {visibilityLabel(one)}
                  <span className="text-xs text-muted-foreground">{VISIBILITY_HINTS[one]}</span>
                </label>
              ))}
              <BoxProblems id={ids.visibility} reasons={boxProblems(problems, 'visibility')} />
              <label className="mt-1 flex items-center gap-2 text-sm">
                <Checkbox
                  checked={isDefault}
                  disabled={!canShare || visibility === 'private'}
                  onCheckedChange={(checked) => setIsDefault(checked === true)}
                />
                Make this {defaultWords(kind, surveyLabel)}
                {canShare && visibility === 'private' && (
                  <span className="text-xs text-muted-foreground">— a shared view’s alone</span>
                )}
              </label>
              <BoxProblems id={`${ids.visibility}-default`} reasons={boxProblems(problems, 'isDefault')} />
              {!canShare && (
                <NotPermitted id={shareReason} what="sharing a view or making one the default" />
              )}
            </fieldset>
          )}
          <div className="flex items-center gap-2 text-sm">
            <span id={`${ids.name}-size-label`}>Rows per page</span>
            <ChoiceSelect
              labelledBy={`${ids.name}-size-label`}
              value={String(size)}
              onChange={(value) => setSize(Number(value))}
              className="h-8 w-28"
              choices={sizes.map((option) => ({
                value: String(option),
                label: option.toLocaleString('en-US')
              }))}
            />
          </div>
          <BoxProblems id={`${ids.name}-size`} reasons={boxProblems(problems, 'pageSize')} />
          {said && (
            <p role="alert" className="text-sm text-destructive">
              {said}
            </p>
          )}
          <DialogFooter className="px-0 pb-0">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? 'Saving…' : 'Save the view'}
            </Button>
          </DialogFooter>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

/** What each visibility means, beside it. */
const VISIBILITY_HINTS: Record<string, string> = {
  private: '— yours alone',
  shared: '— everyone opens it; whoever may share views saves over it',
  shared_read_only: '— everyone opens it; only you save over it'
};

/** *Rename…* — the name and description of the view as saved; nothing else of the screen. */
function RenameDialog({
  open,
  view,
  problems,
  onClose,
  onRename
}: {
  open: boolean;
  view: ISavedView;
  problems: readonly IViewProblem[];
  onClose: () => void;
  onRename: (name: string, description: Nullable<string>) => Promise<void>;
}) {
  const [name, setName] = React.useState(view.name);
  const [description, setDescription] = React.useState(view.description ?? '');
  const [busy, setBusy] = React.useState(false);
  const ids = { name: React.useId(), description: React.useId() };
  const [wasOpen, setWasOpen] = React.useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setName(view.name);
      setDescription(view.description ?? '');
    }
  }
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename the view</DialogTitle>
          <DialogDescription>
            Only the name and description change — the list’s unsaved changes stay unsaved.
          </DialogDescription>
        </DialogHeader>
        <DialogBody
          as="form"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            setBusy(true);
            void onRename(name, description.trim() === '' ? null : description).finally(() => setBusy(false));
          }}
        >
          <div className="grid gap-1.5">
            <Label htmlFor={`${ids.name}-box`}>Name</Label>
            <Input
              id={`${ids.name}-box`}
              value={name}
              onChange={(event) => setName(event.target.value)}
              aria-invalid={boxProblems(problems, 'name').length > 0 || undefined}
              aria-describedby={ids.name}
            />
            <BoxProblems id={ids.name} reasons={boxProblems(problems, 'name')} />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor={`${ids.description}-box`}>Description</Label>
            <Textarea
              id={`${ids.description}-box`}
              value={description}
              rows={2}
              onChange={(event) => setDescription(event.target.value)}
              aria-describedby={ids.description}
            />
            <BoxProblems id={ids.description} reasons={boxProblems(problems, 'description')} />
          </div>
          <DialogFooter className="px-0 pb-0">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? 'Saving…' : 'Rename'}
            </Button>
          </DialogFooter>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The two bridges (the owner's Q7): **Save as a report…** on a list (§3.10) — a new private
 * `report` view with this list's query, columns and sorts, opened on Reports — and **Open as a
 * list** on a report (§4.2) — a new private `list` view of the report's query, opened on Lists. One
 * `POST /views` each, named here; its refusals said in the dialog, the name's at the name.
 */
export function SaveAsReportDialog({
  open,
  onClose,
  survey,
  query,
  to = 'report',
  formattingRules = []
}: {
  open: boolean;
  onClose: () => void;
  survey: string;
  query: IListQuery;
  /** The kind made — a report from a list, a list from a report. */
  to?: SavedViewKind;
  formattingRules?: IListFormattingRule[];
}) {
  const navigate = useNavigate();
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [problems, setProblems] = React.useState<IViewProblem[]>([]);
  const [said, setSaid] = React.useState<Nullable<string>>(null);
  const [busy, setBusy] = React.useState(false);
  const ids = { name: React.useId(), description: React.useId() };
  const [wasOpen, setWasOpen] = React.useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setName('');
      setDescription('');
      setProblems([]);
      setSaid(null);
    }
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setProblems([]);
    setSaid(null);
    try {
      const made = await listsApi.createView({
        kind: to,
        name,
        description: description.trim() === '' ? null : description,
        visibility: 'private',
        query: savedQuery(query),
        formattingRules
      });
      onClose();
      navigate(
        to === 'report'
          ? reportPath(made.body.id)
          : `${TEAM_PATHS.lists}?${new URLSearchParams({ survey, view: made.body.id })}`
      );
    } catch (error) {
      const refusal = viewRefusal(error, 'the save');
      setProblems(refusal.problems);
      const rest = refusal.problems.filter((problem) => !onDialog(problem.target));
      setSaid(
        !(error instanceof ApiError) || error.status >= 500
          ? `The answer did not arrive — open ${to === 'report' ? 'Reports' : 'the view picker on Lists'} to see whether the ${to === 'report' ? 'report' : 'list'} was made before saving again.`
          : rest.length > 0
            ? `The ${to === 'report' ? 'report' : 'list'} could not be made: ${rest.map((problem) => problem.reason).join('; ')}.`
            : refusal.problems.length > 0
              ? null
              : refusal.said.text
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{to === 'report' ? 'Save as a report' : 'Open as a list'}</DialogTitle>
          <DialogDescription>
            {to === 'report'
              ? `A private report of ${listSurveyLabel(survey)} with this list’s filter, columns and sorts — opened on Reports, where it is run and shared.`
              : `A private list of ${listSurveyLabel(survey)} with this report’s filter, columns, sorts and grouping — opened on Lists, where its cells are edited.`}
          </DialogDescription>
        </DialogHeader>
        <DialogBody as="form" onSubmit={(event) => void submit(event)} noValidate>
          <div className="grid gap-1.5">
            <Label htmlFor={`${ids.name}-box`}>Name</Label>
            <Input
              id={`${ids.name}-box`}
              value={name}
              onChange={(event) => setName(event.target.value)}
              aria-invalid={boxProblems(problems, 'name').length > 0 || undefined}
              aria-describedby={ids.name}
            />
            <BoxProblems id={ids.name} reasons={boxProblems(problems, 'name')} />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor={`${ids.description}-box`}>Description</Label>
            <Textarea
              id={`${ids.description}-box`}
              value={description}
              rows={2}
              onChange={(event) => setDescription(event.target.value)}
              aria-describedby={ids.description}
            />
            <BoxProblems id={ids.description} reasons={boxProblems(problems, 'description')} />
          </div>
          {said && (
            <p role="alert" className="text-sm text-destructive">
              {said}
            </p>
          )}
          <DialogFooter className="px-0 pb-0">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? 'Saving…' : to === 'report' ? 'Save the report' : 'Make the list'}
            </Button>
          </DialogFooter>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
